import React from 'react';
import { Box, Typography, alpha } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useGateway } from 'hooks';
import useGatewayAccess from 'features/gateways/hooks/use-gateway-access';

type QuickAccessItem = {
    route: string;
    label: string;
    description: string;
    icon: JSX.Element;
    disabled?: boolean;
};

export default function MenusList() {
    const navigate = useNavigate();
    const gateway = useGateway();
    const {
        isOwner,
        canManageOverallSettings,
        canManageNotificationSettings,
    } = useGatewayAccess();

    const isDedicatedGateway = Boolean(gateway?.is_dedicate);

    const allItems: QuickAccessItem[] = [
        {
            route: 'overall',
            label: 'تنظیمات کلی',
            description: 'مشخصات و تنظیمات اصلی درگاه',
            icon: <i className="fa-light fa-gear" />,
        },
        {
            route: 'switching',
            label: 'سوییچینگ هوشمند',
            description: isDedicatedGateway
                ? 'مدیریت مسیر و اولویت PSPها'
                : 'ویژه درگاه‌های اختصاصی',
            icon: <i className="fa-light fa-shuffle" />,
            disabled: !isDedicatedGateway,
        },
        {
            route: 'namad',
            label: 'نماد اعتماد درگاه پرداخت',
            description: 'تنظیم نمایش و ظاهر نماد اعتماد',
            icon: <i className="fa-light fa-shield-check" />,
        },
        {
            route: 'notifications',
            label: 'تنظیمات اطلاع‌رسانی',
            description: 'مدیریت اعلان‌ها و کانال‌های اطلاع‌رسانی',
            icon: <i className="fa-light fa-bell" />,
        },
        ...(isOwner
            ? [
                  {
                      route: 'admins',
                      label: 'سطح دسترسی‌ها',
                      description: 'مدیریت مدیران و مجوزهای درگاه',
                      icon: <i className="fa-light fa-user-shield" />,
                  } as QuickAccessItem,
              ]
            : []),
        {
            route: 'terminals',
            label: 'جزئیات پایانه‌ها',
            description: 'مشاهده اطلاعات پایانه و پذیرندگی',
            icon: <i className="fa-light fa-list-check" />,
        },
        ...(isOwner
            ? [
                  {
                      route: 'contract',
                      label: 'قرارداد ارائه خدمات',
                      description: 'مشاهده و دریافت قرارداد درگاه',
                      icon: <i className="fa-light fa-file-contract" />,
                  } as QuickAccessItem,
              ]
            : []),
    ];

    const accessibleItems = isOwner
        ? allItems
        : allItems.filter((item) => {
              if (item.route === 'overall') return canManageOverallSettings;
              if (item.route === 'notifications') {
                  return canManageNotificationSettings;
              }

              return false;
          });

    const items =
        gateway?.status === 'activated'
            ? accessibleItems
            : accessibleItems.filter((item) => item.route === 'overall');

    return (
        <Box
            sx={{
                px: 1.35,
                pb: 1.35,
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    lg: '1fr',
                    xl: 'repeat(2, minmax(0, 1fr))',
                },
                gap: 0.85,
            }}>
            {items.map((item, index) => (
                <Box
                    component="button"
                    type="button"
                    key={item.route}
                    disabled={item.disabled}
                    onClick={() =>
                        !item.disabled &&
                        navigate(`/${gateway?.website}/settings/${item.route}`)
                    }
                    sx={(theme) => {
                        const tones = [
                            theme.palette.primary.main,
                            theme.palette.info.main,
                            theme.palette.success.main,
                            theme.palette.warning.main,
                        ];
                        const tone = tones[index % tones.length];

                        return {
                            width: '100%',
                            minWidth: 0,
                            minHeight: 68,
                            border: 0,
                            borderRadius: '13px',
                            bgcolor: item.disabled
                                ? alpha(theme.palette.action.disabledBackground, 0.2)
                                : alpha(tone, theme.palette.mode === 'dark' ? 0.045 : 0.035),
                            color: 'inherit',
                            px: 1.1,
                            py: 0.95,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.9,
                            textAlign: 'right',
                            cursor: item.disabled ? 'not-allowed' : 'pointer',
                            opacity: item.disabled ? 0.52 : 1,
                            transition:
                                'transform .17s ease, background-color .17s ease, box-shadow .17s ease',
                            '&:hover': item.disabled
                                ? {}
                                : {
                                      transform: 'translateY(-2px)',
                                      bgcolor: alpha(
                                          tone,
                                          theme.palette.mode === 'dark' ? 0.085 : 0.07,
                                      ),
                                      boxShadow: `0 10px 24px ${alpha(
                                          theme.palette.common.black,
                                          theme.palette.mode === 'dark' ? 0.12 : 0.055,
                                      )}`,
                                  },
                            '&:focus-visible': {
                                outline: `2px solid ${alpha(theme.palette.primary.main, 0.55)}`,
                                outlineOffset: 2,
                            },
                        };
                    }}>
                    <Box
                        sx={(theme) => {
                            const tones = [
                                {
                                    main: theme.palette.primary.main,
                                    light: theme.palette.primary.light,
                                },
                                {
                                    main: theme.palette.info.main,
                                    light: theme.palette.info.light,
                                },
                                {
                                    main: theme.palette.success.main,
                                    light: theme.palette.success.light,
                                },
                                {
                                    main: theme.palette.warning.main,
                                    light: theme.palette.warning.light,
                                },
                            ];
                            const tone = tones[index % tones.length];
                            const iconColor =
                                theme.palette.mode === 'dark'
                                    ? tone.light
                                    : tone.main;

                            return {
                                width: 36,
                                height: 36,
                                borderRadius: '11px',
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: alpha(
                                    iconColor,
                                    theme.palette.mode === 'dark' ? 0.14 : 0.1,
                                ),
                                color: iconColor,
                                '& i': { fontSize: 14.5 },
                            };
                        }}>
                        {item.icon}
                    </Box>

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65 }}>
                            <Typography
                                className="font-yekan-heavy"
                                sx={{
                                    fontSize: 11.6,
                                    lineHeight: 1.65,
                                    color: 'text.primary',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}>
                                {item.label}
                            </Typography>
                            {item.disabled && (
                                <Typography
                                    sx={(theme) => ({
                                        px: 0.55,
                                        py: 0.1,
                                        borderRadius: '6px',
                                        fontSize: 8.5,
                                        lineHeight: 1.6,
                                        color: 'text.disabled',
                                        bgcolor: alpha(theme.palette.action.disabled, 0.08),
                                        flexShrink: 0,
                                    })}>
                                    غیرفعال
                                </Typography>
                            )}
                        </Box>
                        <Typography
                            sx={{
                                mt: 0.08,
                                fontSize: 9.3,
                                lineHeight: 1.55,
                                color: 'text.secondary',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}>
                            {item.description}
                        </Typography>
                    </Box>

                    <Box
                        sx={(theme) => ({
                            width: 24,
                            height: 24,
                            borderRadius: '8px',
                            color: 'text.disabled',
                            bgcolor: alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.08 : 0.025),
                            fontSize: 9,
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        })}>
                        <i className="fa-light fa-chevron-left" />
                    </Box>
                </Box>
            ))}
        </Box>
    );
}
